import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';

import AppSettingsService from 'services/appSettings';
import CancelModal from 'components/manageSessionModal/cancelModal';
import { toUkDisplayDate } from 'helpers/ukDate';

const OPTIONS = [
    { value: false, label: 'All patients' },
    { value: true, label: 'Own patients only' },
];

// What the selected option means. "Own" is the server's rule (User.ownPatientsOf): anyone booked into
// one of the Doctor's sessions, plus anyone who has never booked. Admins and super admins are never
// narrowed (PatientListScopeService), nor is a Doctor granted full access on the Clinicians page. It is
// a focus feature, not access control: search on the Patients page is never narrowed and a found record
// opens as before - the card must not read as a privacy restriction.
export const FULL_ACCESS_LABEL = 'Always sees all patients';

export const DESCRIPTION_OFF = 'Every Doctor sees all patients and all sessions on their Patients and Appointments '
    + 'pages. Choose "Own patients only" to show each Doctor just their own.';

export const DESCRIPTION_ON = 'Each Doctor\'s Patients and Appointments pages show only their own patients and '
    + 'sessions: anyone who has booked with them, plus patients who haven\'t booked yet. Admins, super admins, '
    + `and Doctors with "${FULL_ACCESS_LABEL}" ticked on the Clinicians page still see everyone. `
    + 'Searching on the Patients page still finds and opens any patient: this changes the default lists, '
    + 'not who can open a record.';

export const CONFIRM_ON_TEXT = 'Every Doctor\'s Patients and Appointments pages will show only their own patients '
    + 'straight away. Admins and super admins always see every patient. Doctors who work across all doctors\' '
    + `lists - ID checks, cover - need "${FULL_ACCESS_LABEL}" ticked on the Clinicians page first.`;

export const COVER_ADVICE = `For cover, tick "${FULL_ACCESS_LABEL}" for the covering Doctor on the Clinicians `
    + 'page (it lasts until you untick it), or switch back to "All patients" to show everyone everything.';

/**
 * The global switch for "own patients only" default lists. superAdmin only. Manual, no expiry:
 * switching it off is also the override for cover situations.
 */
export default function PatientListRestrictionCard() {
    const [setting, setSetting] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [confirmOn, setConfirmOn] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await AppSettingsService.getSettings();
            setSetting(data.patientListRestriction);
            setError('');
        } catch (e) {
            setError('Could not load this setting.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (enabled) => {
        setIsSaving(true);
        try {
            const { data } = await AppSettingsService.setPatientListRestriction(enabled);
            setSetting(data.patientListRestriction);
            setError('');
        } catch (e) {
            setError('Could not change the setting.');
        } finally {
            setIsSaving(false);
            setConfirmOn(false);
        }
    };

    const choose = (enabled) => {
        if (!setting || enabled === setting.enabled || isSaving) {
            return;
        }
        // Switching on narrows every Doctor's lists at once - including Doctors who have not been
        // granted full access yet. Admins and super admins are exempt by role. Ask.
        if (enabled) {
            setConfirmOn(true);
            return;
        }
        save(false);
    };

    return (
        <Card className="card-auth overflow-hidden" data-testid="patient-list-restriction-card">
            <CardBody>
                <label className="details-caption p-2">Patient lists</label>
                <div className="p-2">
                    {error ? <div className="text-danger mb-2" role="alert">{error}</div> : null}

                    {setting ? (
                        <>
                            <div className="mh-options" role="radiogroup" aria-label="Patient lists">
                                {OPTIONS.map(option => (
                                    <label
                                        key={String(option.value)}
                                        className={`mh-option${setting.enabled === option.value ? ' mh-option--selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="patientListRestriction"
                                            checked={setting.enabled === option.value}
                                            disabled={isSaving}
                                            onChange={() => choose(option.value)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>

                            <p className="text-muted font-size-12 mt-2 mb-1" data-testid="patient-list-description">
                                {setting.enabled ? DESCRIPTION_ON : DESCRIPTION_OFF}
                            </p>

                            {/* Cover advice belongs to the ON state: it is the way back to everyone's lists. */}
                            {setting.enabled ? (
                                <p className="text-muted font-size-12 mb-1" data-testid="patient-list-cover">
                                    {COVER_ADVICE}
                                </p>
                            ) : null}

                            {setting.updatedAt ? (
                                <p className="text-muted font-size-12 mb-0" data-testid="patient-list-changed">
                                    {`Last changed${setting.updatedBy ? ` by ${setting.updatedBy.name}` : ''} on ${toUkDisplayDate(setting.updatedAt)}.`}
                                </p>
                            ) : null}
                        </>
                    ) : (!error && <div className="text-muted">Loading…</div>)}
                </div>
            </CardBody>

            <CancelModal
                visibleModal={confirmOn}
                setVisibleModal={setConfirmOn}
                handleClose={() => save(true)}
                header="Show Doctors only their own patients?"
                text={CONFIRM_ON_TEXT}
                cancelButtonTitle="Cancel"
            />
        </Card>
    );
}
